import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, List, ListItem, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image } from '../Image';
import { useEffect, useState } from 'react';

export const SideBar = ({ organizationSideBar, openSidebar, setOpenSidebar }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [expandedParent, setExpandedParent] = useState(null); // State for parent Accordion
  const [expandedChild, setExpandedChild] = useState(null); // State for child Accordion

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const isSuperAdmin = user ? user.superadmin : false;
  const permissions = user && user.permissions ? user.permissions : []; // Safely access permissions

  useEffect(() => {
    setOpenSidebar(false);
  }, [pathname, setOpenSidebar]);

  const handleParentAccordionChange = (id) => (event, isExpanded) => {
    setExpandedParent(isExpanded ? id : null); // Manage parent Accordion state
    if (!isExpanded) {
      setExpandedChild(null); // Close child Accordion when parent is collapsed
    }
  };

  const handleChildAccordionChange = (id) => (event, isExpanded) => {
    setExpandedChild(isExpanded ? id : null); // Manage child Accordion state
  };

  return (
    <List sx={{ p: 0 }}>
      {organizationSideBar
        .filter(menu => isSuperAdmin || permissions.some(permission => permission.includes(menu.id))) // Conditionally render parent menus
        .map((menu) => (
          <ListItem
            key={menu.id}
            onClick={() => {
              setOpenSidebar(true);
            }}
            sx={{ p: 0 }}
          >
            <Accordion
              id={`menu-l0-${menu.id}`}
              disableGutters
              sx={{
                boxShadow: 'none',
                borderRadius: 0,
                background: pathname === menu.to ? '#80b4d1' : 'linear-gradient(to right, #b2e4ed, #a0d3e8)', // Darker background for active route
                flex: 1,
                '& .MuiAccordionSummary-content': {
                  my: 0.5
                },
                '&:hover, &.child-item:hover': {
                  backgroundColor: '#E0F7F7'
                }
              }}
              expanded={expandedParent === menu.id}
              onChange={handleParentAccordionChange(menu.id)}
            >
              <AccordionSummary
                sx={{
                  m: 0,
                  minHeight: '0'
                }}
                {...(openSidebar &&
                  (!menu.children
                    ? {
                      onClick: () => navigate(menu.to)
                    }
                    : { expandIcon: <ExpandMore fontSize="large" /> }))}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Image src={menu.img.default} alt={menu.label} sx={{ width: '2.5rem' }} />
                  {openSidebar && <Typography variant="h6" sx={{ fontWeight: 700 }}>{menu.label}</Typography>}
                </Stack>
              </AccordionSummary>

              {!!menu.children?.length && openSidebar && (
                <AccordionDetails sx={{ p: 0 }}>
                  {menu.children
                    .filter(child => isSuperAdmin || permissions.some(permission => permission.includes(child.id))) // Conditionally render child menus
                    .map((child) => (
                      <Accordion
                        id={`menu-l0-${child.id}`}
                        disableGutters
                        sx={{
                          boxShadow: 'none',
                          borderRadius: 0,
                          background: pathname === child.to ? '#80b4d1' : 'linear-gradient(to right, #b2e4ed, #a0d3e8)', // Darker background for active child route
                        }}
                        expanded={expandedChild === child.id}
                        onChange={handleChildAccordionChange(child.id)}
                      >
                        <AccordionSummary
                          sx={{ m: 0, minHeight: '0' }}
                          {...(!child.children
                            ? {
                              onClick: () => navigate(child.to)
                            }
                            : { expandIcon: <ExpandMore fontSize="large" /> })}
                        >
                          {openSidebar && (
                            <Typography variant="h6" sx={{ pl: 2, fontWeight: 700 }}>
                              {child.label}
                            </Typography>
                          )}
                        </AccordionSummary>

                        {!!child.children?.length && (
                          <AccordionDetails sx={{ p: 0 }}>
                            {child.children
                              .filter(data => isSuperAdmin || permissions.some(permission => permission.includes(data.id))) // Conditionally render grandchild menus
                              .map((data) => (
                                <Typography
                                  variant="h6"
                                  key={data.id}
                                  sx={{
                                    my: 1,
                                    fontWeight: 700,
                                    pl: 6,
                                    cursor: 'pointer',
                                    background: pathname === data.to ? '#80b4d1' : 'transparent', // Darker background for active grandchild route
                                    '&.child-item:hover': {
                                      backgroundColor: '#E0F7F7'
                                    }
                                  }}
                                  onClick={() => navigate(data.to)}
                                  className="child-item"
                                >
                                  {data.label}
                                </Typography>
                              ))}
                          </AccordionDetails>
                        )}
                      </Accordion>
                    ))}
                </AccordionDetails>
              )}
            </Accordion>
          </ListItem>
        ))}
    </List>
  );
};
